<template>
  <v-container fluid>
    <h1 class="headline">Itinerary Documents</h1>
    <v-row>
      <v-col>
        <v-card>
          <v-data-table
            :headers="headers"
            :items="items"
            :loading="$apollo.loading"
          >
            <template v-slot:item.actions="{ item }">
              <v-row justify="center">
                <v-btn icon small class="mx-0" @click="onAvailDocDetails(item)">
                  <v-icon small color="cyan">mdi-file-find</v-icon>
                </v-btn>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn small icon class="mx-0" @click="onDocEdit(item)">
                      <v-icon color="teal" v-on="on" small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit Document</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      small
                      icon
                      class="mx-0"
                      @click="onContentsEdit(item)"
                    >
                      <v-icon color="blue" v-on="on" small>mdi-image</v-icon>
                    </v-btn>
                  </template>
                  <span>Document Template Contents</span>
                </v-tooltip>
                <confirmation-dialog @onConfirm="onRemoveDoc(item)">
                  <v-btn icon small class="mx-0">
                    <v-icon small color="pink">mdi-delete</v-icon>
                  </v-btn>
                  <template slot="message">This will remove Document</template>
                </confirmation-dialog>
              </v-row>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <basic-dialog
      :show="showContentEditor || showDocEditor"
      @close="onDialogClose"
    >
      <contents
        v-if="showContentEditor"
        owner-name="AVAILABLEDOCUMENT"
        :owner-id="editedDoc ? editedDoc.id : 0"
      />
      <v-row v-if="showDocEditor">
        <v-col cols="12">
          <v-row>
            <v-col>
              <v-text-field
                label="Document Name"
                v-model="editedDoc.name"
                :outlined="outlinedInputs"
              />
            </v-col>
          </v-row>
          <h5 class="subtitle-1">Custom Fields</h5>
          <v-row>
            <custom-fields-form
              v-model="editedDoc.customFields"
              hide-save
            ></custom-fields-form>
          </v-row>
        </v-col>
      </v-row>
      <template v-slot:title>{{ contentEditorTitle }}</template>
      <template v-if="showDocEditor" v-slot:actions>
        <v-btn color="primary" :disabled="!isValid" @click="onSave">Save</v-btn>
      </template>
    </basic-dialog>
    <document-builder
      v-if="selectedDocument"
      :show="showDocumentBuilderDialog"
      @close="showDocumentBuilderDialog = false"
      v-model="selectedDocument"
      @saved="onDocSaved"
    />
    <v-row v-if="debug">
      <v-col>
        <code>{{ $data }}</code>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  AVAILABLE_DOCUMENTS_LIST_WITH_CF,
  UPDATE_AVAILABLE_DOC,
} from "../../../../constants/graphql";
import { removeGQLTypename } from "../../../../helpers";
import BasicDialog from "../../shared/BasicDialog";
import Contents from "../../shared/contents/Contents";
import CustomFieldsForm from "../../shared/CustomFieldsForm";
import DocumentBuilder from "../documents/DocumentBuilder";
import ConfirmationDialog from "../../shared/ConfirmationDialog";
import gql from "graphql-tag";
const REMOVE_DOC = gql`
  mutation removeOwnerDoc($id: ID!) {
    deleteDocumentType(id: $id) {
      id
    }
  }
`;
export default {
  name: "itinerary-documents",

  components: {
    BasicDialog,
    Contents,
    CustomFieldsForm,
    DocumentBuilder,
    ConfirmationDialog,
  },

  data() {
    return {
      debug: false,
      editedDoc: null,
      showContentEditor: false,
      showDocEditor: false,
      selectedDocument: null,
      showDocumentBuilderDialog: false,
      headers: [
        {
          text: "Template Name",
          sortable: true,
          value: "name",
          name: "name",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
    };
  },

  computed: {
    items() {
      return this.availableDocuments && this.availableDocuments.edges
        ? this.availableDocuments.edges.map(removeGQLTypename)
        : [];
    },
    contentEditorTitle() {
      return this.editedDoc
        ? `${this.editedDoc.name} ${
            this.showContentEditor ? "- Contents" : "- Edit"
          }`
        : "Edit Contents";
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    isValid() {
      return this.editedDoc && this.editedDoc.name != "";
    },
  },

  methods: {
    onRemoveDoc(item) {
      this.$apollo
        .mutate({
          mutation: REMOVE_DOC,
          variables: { id: item.id },
          refetchQueries: [
            {
              query: AVAILABLE_DOCUMENTS_LIST_WITH_CF,
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Document Removed.",
          });
        })
        .catch(this.errorHandler);
    },
    onAvailDocDetails(item) {
      console.log("viewing doc: ", item);
      this.selectedDocument = { id: item.docId, name: item.name };
      this.showDocumentBuilderDialog = true;
    },
    onSave() {
      this.$apollo
        .mutate({
          mutation: UPDATE_AVAILABLE_DOC,
          variables: { input: { ...this.editedDoc } },
        })
        .then((result) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Changes Saved.",
            color: "success",
          });
          this.showDocEditor = false;
        })
        .catch((err) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: error,
            color: "warning",
          });
          this.showDocEditor = false;
        });
    },
    onContentsEdit(doc) {
      this.editedDoc = { ...doc };
      this.showContentEditor = true;
    },
    onDocEdit(doc) {
      this.editedDoc = {
        ...doc,
        customFields: doc.customFields.edges.map(removeGQLTypename),
      };
      this.showDocEditor = true;
    },
    onDialogClose() {
      this.showContentEditor = false;
      this.showDocEditor = false;
    },
    onCustomFieldsChange(payload) {
      console.log(payload);
    },
    onDocSaved() {
      if (this.$apollo.queries.availableDocuments != null) {
        this.$apollo.queries.availableDocuments.refetch();
      }
    },
  },

  apollo: {
    availableDocuments: {
      query: AVAILABLE_DOCUMENTS_LIST_WITH_CF,
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/admin/contents/ItineraryDocuments.vue